import { Badge, Text, Flex, Link, Table, IconButton } from "@radix-ui/themes";
import { Application } from "@/types/shared";
import { badgeColors } from "../constants/constants";
import WithdrawFromEventButton from "../WithdrawFromEventButton";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useState, useMemo } from "react";
import { ArrowDownAZ, ArrowDownUp, ArrowUpAZ, ArrowUpZA } from "lucide-react";
import ColumnFilter from "./ColumnFilter";
import { dateRangeFilter } from "./utility";

interface Props {
  applications: Application[];
}
export default function ProfileApplicationsTable({ applications }: Props) {
  const [data, setData] = useState(() => [...applications]);
  const columnHelper = createColumnHelper<Application>();

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columns = useMemo<ColumnDef<Application, any>[]>(
    () => [
      columnHelper.accessor("event_id", {
        header: "Name",
        accessorFn: (row) => `${row.event_name} ${row.event_location}`,
        cell: (props) => (
          <Flex direction="column">
            <Link href={`/events/${props.row.original.event_id}`}>
              <Text size="3">{props.row.original.event_name}</Text>
            </Link>
            <Text color="gray" size="1">
              {props.row.original.event_location}
            </Text>
          </Flex>
        ),
      } as AccessorFnColumnDef<Application, any>),
      columnHelper.accessor("event_date", {
        header: "Date",
        filterFn: dateRangeFilter,
        meta: {
          filterVariant: 'dateRange'
        }
      }),
      columnHelper.accessor("created_at", {
        header: "Applied On",
        filterFn: dateRangeFilter,
        meta: {
          filterVariant: 'dateRange'
        }
      }),
      columnHelper.accessor("participant.type", {
        header: "As",
        meta: {
          filterVariant: 'select',
          selectOptions: [
            { value: "all", title: "All" },
            { value: "MediaGroup", title: "Media" },
            { value: "DriverCar", title: "Driver" },
            { value: "Staff", title: "Staff" },
            { value: "Team", title: "Team" },
          ]
        }
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (props) => (
          <Badge size="3" variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: 'select',
          selectOptions: [
            { value: "all", title: "All" },
            { value: "pending", title: "Pending" },
            { value: "accepted", title: "Accepted" },
            { value: "rejected", title: "Rejected" },
            { value: "withdrawn", title: "Withdrawn" },
          ]
        }
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (props) => (
          <Flex gap="2">
            <WithdrawFromEventButton application={props.row.original} />
          </Flex>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  return (
    <>
      <Table.Root>
        <Table.Header>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.ColumnHeaderCell key={header.id}>
                  <Flex justify="between">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    <Flex gap="2">
                      {header.column.getCanSort() ? (
                        <IconButton
                          size="1"
                          variant="soft"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {{
                            asc: <ArrowDownAZ size={"16px"} />,
                            desc: <ArrowUpZA size={"16px"} />,
                          }[header.column.getIsSorted() as string] ?? (
                            <ArrowDownUp size="16px" />
                          )}
                        </IconButton>
                      ) : null}

                      {header.column.getCanFilter() ? (
                        <ColumnFilter column={header.column} />
                      ) : null}
                    </Flex>
                  </Flex>
                </Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Cell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </>
  );
}
